'use client';

import React, { useRef, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import gsap from 'gsap';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import styles from './UltraSnapSection.module.css';
import { drawImageSafe } from 'utils/drawImageSafe';

const SEQUENCE_PATH = '/web_v2/ring-pro/landing-page/sequence/ultra-snap';
const TOTAL_FRAMES = 148; // frames 0000–0147
const CONCURRENCY = 8;

// 0 = all frames, 1 = skip 1 (load every 2nd), 2 = skip 2 (load every 3rd), etc.
const FRAME_STEP = 0;

const frameUrl = (index: number) =>
  getCompressedAssetUrl(
    `${SEQUENCE_PATH}/${index.toString().padStart(4, '0')}.png`,
  );

const buildFrameIndices = () => {
  const step = FRAME_STEP + 1;
  const indices: number[] = [];
  for (let i = 0; i < TOTAL_FRAMES; i += step) {
    indices.push(i);
  }
  return indices;
};

const UltraSnapSection = ({
  skipChargerWait = false,
}: {
  skipChargerWait?: boolean;
}) => {
  const t = useTranslations('ring-pro');
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const imagesRef = useRef<HTMLImageElement[]>([]);
  const timelineRef = useRef<gsap.core.Tween | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastDrawnFrameRef = useRef(-1);
  const renderState = useRef({ frame: 0 });

  const [isReady, setIsReady] = useState(false);
  const [isFirstFrameReady, setIsFirstFrameReady] = useState(false);

  /* ── Preload frames ─────────────────────────────────── */
  /* First frame loads immediately; remaining frames wait for ChargerIntro */

  useEffect(() => {
    let isCancelled = false;
    const frameIndices = buildFrameIndices();
    const loaded: HTMLImageElement[] = new Array(frameIndices.length);

    const loadOne = (arrayIdx: number) =>
      new Promise<void>((resolve) => {
        const img = new Image();
        img.loading = 'eager';
        img.src = frameUrl(frameIndices[arrayIdx]);
        img.onload = img.onerror = () => {
          loaded[arrayIdx] = img;
          resolve();
        };
      });

    // Load first frame immediately so canvas is never blank
    loadOne(0).then(() => {
      if (isCancelled) return;
      imagesRef.current = loaded;
      setIsFirstFrameReady(true);
    });

    // Load remaining frames after ChargerIntro finishes
    const loadRemaining = () => {
      if (isCancelled) return;
      let nextIndex = 1; // skip 0, already loaded
      let inflight = 0;

      const pump = () => {
        if (isCancelled) return;
        if (nextIndex >= frameIndices.length && inflight === 0) {
          imagesRef.current = loaded;
          console.log(
            `%c UltraSnap %c Loaded ${loaded.length} images (FRAME_STEP=${FRAME_STEP}) `,
            'background:#7c3aed;color:#fff;border-radius:3px 0 0 3px;padding:2px 6px;font-weight:bold',
            'background:#1e1e2e;color:#a5f3fc;border-radius:0 3px 3px 0;padding:2px 6px',
          );
          setIsReady(true);
          return;
        }
        while (inflight < CONCURRENCY && nextIndex < frameIndices.length) {
          const idx = nextIndex;
          nextIndex++;
          inflight++;
          loadOne(idx).then(() => {
            inflight--;
            pump();
          });
        }
      };

      pump();
    };

    if (skipChargerWait || (window as any).__chargerIntroLoaded) {
      loadRemaining();
    } else {
      window.addEventListener('charger-intro-loaded', loadRemaining, {
        once: true,
      });
    }

    return () => {
      isCancelled = true;
      window.removeEventListener('charger-intro-loaded', loadRemaining);
    };
  }, []);

  /* ── Canvas setup & first frame ──────────────────────── */

  const drawFrame = (frameIdx: number) => {
    const ctx = ctxRef.current;
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;

    const rounded = Math.round(frameIdx);
    if (lastDrawnFrameRef.current === rounded) return;

    const img = imagesRef.current[rounded];
    if (!img || !img.complete || img.naturalWidth === 0) return;

    const dpr = window.devicePixelRatio || 1;
    const displayW = canvas.width / dpr;
    const displayH = canvas.height / dpr;

    ctx.clearRect(0, 0, displayW, displayH);
    if (!drawImageSafe(ctx, img, 0, 0, displayW, displayH)) return;
    lastDrawnFrameRef.current = rounded;
  };

  useIsomorphicLayoutEffect(() => {
    if (!isFirstFrameReady || !canvasRef.current) return;
    const canvas = canvasRef.current;

    const ctx = canvas.getContext('2d', { desynchronized: true });
    if (!ctx) return;
    ctxRef.current = ctx;

    const updateSize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      lastDrawnFrameRef.current = -1;
      drawFrame(renderState.current.frame);
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    drawFrame(0);

    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, [isFirstFrameReady]);

  /* ── GSAP timeline + IntersectionObserver ─────────────── */

  useIsomorphicLayoutEffect(() => {
    if (!isReady || !sectionRef.current || !canvasRef.current) return;

    const scrub = renderState.current;

    const startAnimation = () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
      scrub.frame = 0;
      drawFrame(0);

      timelineRef.current = gsap.to(scrub, {
        frame: imagesRef.current.length - 1,
        snap: 'frame',
        ease: 'linear',
        duration: 5,
        onUpdate: () => {
          requestAnimationFrame(() => drawFrame(scrub.frame));
        },
        onComplete: () => {
          loopTimeoutRef.current = setTimeout(() => {
            startAnimation();
          }, 3000);
        },
      });
    };

    const stopAnimation = () => {
      if (loopTimeoutRef.current) clearTimeout(loopTimeoutRef.current);
      if (timelineRef.current) {
        timelineRef.current.kill();
        timelineRef.current = null;
      }
      scrub.frame = 0;
      lastDrawnFrameRef.current = -1;
      drawFrame(0);
    };

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].intersectionRatio > 0) {
          startAnimation();
        } else {
          stopAnimation();
        }
      },
      { root: null, rootMargin: '0px', threshold: 0.01 },
    );

    observerRef.current.observe(sectionRef.current);

    return () => {
      stopAnimation();
      observerRef.current?.disconnect();
    };
  }, [isReady]);

  return (
    <section className={styles.section} ref={sectionRef}>
      <div className={styles.textContainer}>
        <h2 className={styles.heading}>
          {t('ring-pro.ultraSnap.headingBrand')}
          <span>&trade;</span> {t('ring-pro.ultraSnap.headingText')}
        </h2>
        <p className={styles.description}>
          {t('ring-pro.ultraSnap.description')}
        </p>
      </div>

      <div className={styles.canvasContainer}>
        <canvas className={styles.canvas} ref={canvasRef} />
      </div>
    </section>
  );
};

export default UltraSnapSection;
